<template>
    <div>
        <div class="formation mt-2 container" data-test="menu-list">
            <div class="row justify-content-center">

                <div
                    class="col-md-3 col-12 d-sm-none d-md-block"
                >
                    <loader v-if="isLoading" data-type="FormationEditMenuLoader" />

                    <div
                        v-else
                        class="
                            mt-1
                            position-fixed
                            display-sm-none
                        "
                    >
                        <h2
                            :class="currentStep === 1 && 'text-primary'"
                            class="mb-2"
                        >
                            <feather-icon
                                class="info-icon mr-.5"
                                icon="InfoIcon"
                                width="15"
                                height="15"
                            />
                            <span :style="dynamicStyle" data-test="step-information" @click="getCurrentStep(1)">
                                Company Information
                            </span>
                        </h2>

                        <h2
                            class="mb-2"
                            :class="currentStep === 2 && 'text-primary'"
                        >
                            <vector
                                data-type="icon"
                                data-width="15px"
                                data-height="15px"
                                data-name="users"
                                data-class="mr-.5"
                            />
                            <span :style="dynamicStyle" data-test="step-shareholder" @click="getCurrentStep(2)">
                               Shareholders 
                               
                               <span v-if="requiredPOACount > 0" data-test="required-poa-count"> 
                                    <b-badge class="rounded-circle" pill variant="danger" style="width: 19px; position:relative; bottom: 4px; right: 4px;"> 
                                        {{ requiredPOACount }} 
                                    </b-badge>
                               </span>
                            </span>
                        </h2>

                        <h2
                            class="mb-2"
                            :class="currentStep === 3 && 'text-primary'"
                        >
                            <vector
                                data-type="icon"
                                data-width="15px"
                                data-height="15px"
                                data-name="director"
                                data-class="mr-.5"
                            />
                            <span :style="dynamicStyle" data-test="step-director-and-shareholder" @click="getCurrentStep(3)">
                                Directors
                            </span>
                        </h2>

                        <h2
                            class="mb-2"
                            :class="currentStep === 4 && 'text-primary'"
                        >
                            <vector
                                data-width="15px"
                                data-height="15px"
                                data-class="mr-.5 no-path-fill"
                                data-name="key"
                            ></vector>
                            <span :style="dynamicStyle" data-test="step-beneficial-owner" @click="getCurrentStep(4)">
                                Beneficial Owners
                            </span>
                        </h2>

                        <h2
                            class="mb-2"
                            :class="currentStep === 5 && 'text-primary'"
                        >
                            <vector
                                data-width="15px"
                                data-height="15px"
                                data-name="controller"
                                data-class="mr-.5 no-path-fill"
                            ></vector>
                            <span :style="dynamicStyle" data-test="step-registrable-controller" @click="getCurrentStep(5)">
                                Registrable Controllers
                            </span>
                        </h2>
                       
                        <h2
                            class="mb-2"
                            :class="currentStep === 6 && 'text-primary'"
                        >
                            <vector
                                data-width="15px"
                                data-height="15px"
                                data-type="icon"
                                data-class="mr-.5"
                                data-name="form-sign"
                            ></vector>
                            <span :style="dynamicStyle" data-test="step-complete-transfer-company" @click="getCurrentStep(6)">
                                Complete Transfer
                            </span>
                        </h2>
                    </div>
                </div>

                <div class="col-md-9 col-12 p-0">
                    <transition name="zoom-fade" mode="out-in">
                        <component
                            :is="getCurrentComponent"
                            :ref="getCurrentComponentName"
                            :require-poa-count="requiredPOACount"
                            @next="nextStep"
                            @previous="previousStep"
                        />
                    </transition>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { BBadge } from 'bootstrap-vue';
import { mapGetters } from 'vuex';
import { COMPANY } from '@/constants/config';
import { updateCurrentCompanyInStore } from '@/helpers';
import CompanyInformation from './partials/CompanyInformation.vue';
import Shareholders from './partials/Shareholder.vue';
import Directors from './partials/Director.vue';
import BeneficialOwners from './partials/BeneficialOwner.vue';
import RegistrableControllers from './partials/RegistrableController.vue';
import CompleteTransferCompany from './partials/CompleteTransferCompany.vue';

export default {
    components: {
        BBadge, 
        // components
        CompanyInformation,
        Shareholders,
        Directors,
        BeneficialOwners,
        RegistrableControllers,
        CompleteTransferCompany
    },

    data() {
        return {
            currentCompany: {
                remark: undefined,
            },
            isLoading: false,
            currentStep: 1,
            steps: ["company-information", "shareholders", "directors", "beneficial-owners", "registrable-controllers", "complete-transfer-company"],
        };
    },

    computed: {
        ...mapGetters({
            companyStatus: 'company/companyStatus',
            requiredPOACount: 'shareholder/requiredPOACount'
        }),

        getCurrentComponent() {
            const page = this.steps[this.currentStep - 1];
            this.$store.commit('company/UPDATE_CURRENT_ONBOARDING_PAGE', page);
            return page;
        },

        getCurrentComponentName()
        {
            return this.steps[this.currentStep - 1];
        },

        dynamicStyle() {
            if(
                this.companyStatus !== COMPANY.STATUS_TRANSFER_COMPANY_IN_PROGRESS ||
                this.currentCompany.remark === COMPANY.GENERATED_TRANSFER_COMPANY_REMARK
            ) 
                return "cursor: pointer";

            return "cursor: auto";
        }
    },

    async created() {
        this.isLoading = true;
        this.$store.commit('shareholder/RESET_POA_COUNT');
        
        // update the company in store
        await updateCurrentCompanyInStore()
            .then(company => {
                this.currentCompany.remark = company.company_information?.remark;

                this.isLoading = false;
            })
            .catch(error => {
                this.isLoading = false;
                // eslint-disable-next-line no-console
                console.log("Error while updating current company in store", error);
            });
        
        // shareholder require poa count to show
        this.$store.dispatch('shareholder/REQUIRE_POA').catch(() => {});
    },

    methods: {
        nextStep() {
            if (this.currentStep < 6) this.currentStep += 1;
            this.$store.dispatch('shareholder/REQUIRE_POA');
        },

        previousStep() {
            if (this.currentStep > 1) this.currentStep -= 1;
            this.$store.dispatch('shareholder/REQUIRE_POA');
        },

        getCurrentStep(step) {
            if(
                step &&
                (
                    this.companyStatus !== COMPANY.STATUS_TRANSFER_COMPANY_IN_PROGRESS ||
                    this.currentCompany.remark === COMPANY.GENERATED_TRANSFER_COMPANY_REMARK
                )
            ) {
                this.currentStep = step;
                this.$store.dispatch('shareholder/REQUIRE_POA').catch(() => {});
            }
        },
    },
};
</script>

<style>
.formation h2
{
    display: flex;
    align-items: center;
    font-size: calc(1.155rem - 2px);
}

.formation .side-map
{
    position: relative;
    left: -30px;
}

@media screen and (max-width: 1200px)
{
    .formation h2
    {
        font-size: 1rem;
    }

    .formation h2 svg
    {
        width: 13px !important;
        height: 13px !important;
        margin-right: .5rem !important;
    }

}
</style>
